import { useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  addLanguageToPath, 
  extractLanguageFromPath, 
  LanguageCode,
  isValidLanguageCode 
} from "../utils/languageRouting";

/**
 * Custom hook for language-aware navigation
 * Automatically adds language code to URLs when navigating
 */
export const useLanguageNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  // Get language from URL first, then from i18n
  const { language: urlLanguage } = extractLanguageFromPath(location.pathname);
  const currentLanguage = (urlLanguage || i18n.language) as LanguageCode;

  /**
   * Navigate to a path with language code
   */
  const navigateWithLanguage = useCallback((
    to: string, 
    options?: { 
      replace?: boolean;
      state?: any;
      usePrefix?: boolean; // true for /it/professional, false for /professional/it
    }
  ) => {
    const position = options?.usePrefix !== false ? "prefix" : "suffix";
    
    // Get current language from URL or i18n
    const { language: urlLang } = extractLanguageFromPath(location.pathname);
    const activeLang = (urlLang || i18n.language) as LanguageCode;
    
    // Don't add language code to English (default)
    const pathWithLanguage = activeLang === "en" 
      ? to 
      : addLanguageToPath(to, activeLang, position);

    navigate(pathWithLanguage, {
      replace: options?.replace,
      state: options?.state,
    });
  }, [navigate, location.pathname, i18n.language]);

  /**
   * Get current path without language code
   */
  const getCurrentPath = useCallback(() => {
    const { cleanPath } = extractLanguageFromPath(location.pathname);
    return cleanPath;
  }, [location.pathname]);

  /**
   * Get current language from URL
   */
  const getCurrentLanguageFromURL = useCallback(() => {
    const { language } = extractLanguageFromPath(location.pathname);
    return language || "en";
  }, [location.pathname]);

  /**
   * Get a path with the current language prefix
   * Useful for creating language-aware links
   */
  const getPathWithLanguage = useCallback((path: string) => {
    const { language: urlLang } = extractLanguageFromPath(location.pathname);
    const activeLang = (urlLang || i18n.language) as LanguageCode;
    
    return activeLang === "en" 
      ? path 
      : addLanguageToPath(path, activeLang, "prefix");
  }, [location.pathname, i18n.language]);

  return {
    navigateWithLanguage,
    getCurrentPath,
    getCurrentLanguageFromURL,
    getPathWithLanguage,
    currentLanguage,
  };
};

/**
 * Hook to sync i18n language with URL
 * Automatically updates i18n when URL language changes
 */
export const useLanguageSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    const { language } = extractLanguageFromPath(location.pathname);
    
    // Normalize current i18n language (en-US -> en)
    const normalizedI18nLang = i18n.language.split("-")[0];
    
    // Priority: URL language > i18n language (which comes from localStorage)
    if (language) {
      // URL has a language code
      if (language !== normalizedI18nLang) {
        i18n.changeLanguage(language);
        localStorage.setItem("i18nextLng", language);
      }
    }
    // If no language in URL, i18n keeps its current language (from localStorage)
    // No action needed - i18n already loaded from localStorage on init
  }, [location.pathname, i18n]);

  /**
   * Change language and update URL
   */
  const changeLanguage = useCallback(async (
    language: LanguageCode,
    options?: { usePrefix?: boolean }
  ) => {
    const { cleanPath } = extractLanguageFromPath(location.pathname);
    const position = options?.usePrefix !== false ? "prefix" : "suffix";
    
    // Update i18n
    await i18n.changeLanguage(language);
    localStorage.setItem("i18nextLng", language);

    // Update URL
    if (language === "en") {
      // For English, remove language code from URL
      navigate(cleanPath, { replace: true });
    } else {
      // For other languages, add language code
      const newPath = addLanguageToPath(cleanPath, language, position);
      navigate(newPath, { replace: true });
    }
  }, [location.pathname, i18n, navigate]);

  return {
    changeLanguage,
    currentLanguage: i18n.language.split("-")[0] as LanguageCode,
  };
};

